import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import { ProgressSpinner } from 'primereact/progressspinner';
import type { Responsable } from '../../negocio/negocio.types';
import { ResponsablesService } from '../services/responsables.service';
import { RoleService } from '../../layouts/services/role.service';
import { etiquetaVigencia, filtrarResponsables, llaveTienda, parsearTiendasParam } from '../utils/responsables';
import { opcionesDeResponsables } from '../../perfiles/utils/perfiles';
import { mensajeDeError, noDisponible } from '../../negocio/errores';
import { ReasignarDialog } from '../components/ReasignarDialog';
import { HistorialDialog } from '../components/HistorialDialog';

const CLASE_CAMPO =
  'mt-1 w-full rounded-md border border-solid border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 focus:border-blue-500 focus:outline-none';

const ordenar = (xs: Responsable[]) =>
  [...xs].sort((a, b) => a.cadena.localeCompare(b.cadena) || a.num - b.num);

/**
 * Responsables por tienda (Consulta CM, fase 1): supervisor y regional de cada
 * tienda, con historial. La lista la lee TI (y `indicadores`, por la API); sólo
 * superadmin y admin reasignan y ven el historial.
 *
 * `?tiendas=TAF-118,VAZ-1` llega desde «Dar de baja (rotación)»: muestra sólo
 * las tiendas que la baja dejó sin responsable.
 */
const ResponsablesScreen: React.FC = () => {
  const [params, setParams] = useSearchParams();
  const delaBaja = useMemo(() => parsearTiendasParam(params.get('tiendas')), [params]);
  const esTI = RoleService.isAdmin();

  const [lista, setLista] = useState<Responsable[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sinRuta, setSinRuta] = useState(false);
  const [texto, setTexto] = useState('');
  const [regional, setRegional] = useState('');
  const [supervisor, setSupervisor] = useState('');
  const [soloSin, setSoloSin] = useState(false);
  const [reasignando, setReasignando] = useState<Responsable | null>(null);
  const [historial, setHistorial] = useState<Responsable | null>(null);

  const cargar = useCallback(async () => {
    setError(null);
    try {
      setLista(ordenar(await ResponsablesService.listar()));
    } catch (e) {
      if (noDisponible(e)) setSinRuta(true);
      else setError(mensajeDeError(e));
    }
  }, []);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  const opciones = useMemo(() => opcionesDeResponsables(lista ?? []), [lista]);
  const visibles = useMemo(
    () =>
      filtrarResponsables(lista ?? [], {
        texto,
        regional: regional || null,
        supervisor: supervisor || null,
        soloSinResponsable: soloSin,
        tiendas: delaBaja,
      }),
    [lista, texto, regional, supervisor, soloSin, delaBaja],
  );

  const verTodas = () => {
    const p = new URLSearchParams(params);
    p.delete('tiendas');
    setParams(p, { replace: true });
  };

  if (sinRuta) {
    return (
      <Message
        severity="info"
        className="w-full justify-start"
        text="La API de este entorno todavía no ofrece Responsables por tienda (llega con Consulta CM). Mientras tanto, supervisor y regional salen del catálogo compilado."
      />
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Responsables por tienda</h1>
        <p className="mt-1 text-sm text-gray-600">
          Supervisor y regional de cada tienda. ⚠️ Los nombres son llaves de acceso de Metas: reasignar mueve quién ve,
          captura y autoriza. El encabezado de «Mi tienda» y los filtros de Indicadores todavía leen el catálogo
          compilado.
        </p>
      </div>

      {delaBaja.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-solid border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          <span>
            Mostrando las {delaBaja.length} tiendas que dejó la baja sin responsable. Asigne a quien lo sustituye.
          </span>
          <Button label="Ver todas" className="p-button-text p-button-sm" onClick={verTodas} />
        </div>
      )}

      <div className="grid grid-cols-1 gap-3 rounded-md border border-solid border-gray-200 bg-white p-3 sm:grid-cols-2 lg:grid-cols-4">
        <div>
          <label className="block text-sm font-medium text-gray-700" htmlFor="resp-buscar">
            Buscar tienda o persona
          </label>
          <input
            id="resp-buscar"
            className={CLASE_CAMPO}
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            placeholder="VAZ 1, Regina, Sandra…"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700" htmlFor="resp-regional">
            Regional
          </label>
          <select id="resp-regional" className={CLASE_CAMPO} value={regional} onChange={(e) => setRegional(e.target.value)}>
            <option value="">Todos</option>
            {opciones.regionales.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700" htmlFor="resp-supervisor">
            Supervisor
          </label>
          <select
            id="resp-supervisor"
            className={CLASE_CAMPO}
            value={supervisor}
            onChange={(e) => setSupervisor(e.target.value)}
          >
            <option value="">Todos</option>
            {opciones.supervisores.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </div>
        <label className="flex items-center gap-2 self-end pb-2 text-sm text-gray-700" htmlFor="resp-sin">
          <input id="resp-sin" type="checkbox" checked={soloSin} onChange={(e) => setSoloSin(e.target.checked)} />
          Sólo sin supervisor o regional
        </label>
      </div>

      {error && <Message severity="error" text={error} className="w-full justify-start" />}
      {!lista && !error && (
        <div className="flex justify-center p-6">
          <ProgressSpinner style={{ width: '2.5rem', height: '2.5rem' }} />
        </div>
      )}

      {lista && (
        <div className="overflow-x-auto rounded-md border border-solid border-gray-200 bg-white">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-3 py-2">Tienda</th>
                <th className="px-3 py-2">Supervisor</th>
                <th className="px-3 py-2">Regional</th>
                <th className="px-3 py-2">Vigencia</th>
                {esTI && <th className="px-3 py-2 text-right">Acciones</th>}
              </tr>
            </thead>
            <tbody>
              {visibles.map((r) => {
                const nombre = `${r.cadena} ${r.num}`;
                return (
                  <tr
                    key={llaveTienda(r)}
                    data-testid="fila-responsable"
                    data-tienda={llaveTienda(r)}
                    className="border-t border-solid border-gray-100"
                  >
                    <td className="px-3 py-2">
                      <b>{nombre}</b> <span className="text-gray-600">{r.tienda}</span>
                    </td>
                    <td className="px-3 py-2">{r.supervisor ?? <span className="text-amber-700">Sin supervisor</span>}</td>
                    <td className="px-3 py-2">{r.regional ?? <span className="text-amber-700">Sin regional</span>}</td>
                    <td className="px-3 py-2 text-gray-600">
                      {etiquetaVigencia(r)}
                      {r.origen === 'catalogo' && <span className="ml-1 text-xs text-gray-400">(catálogo)</span>}
                    </td>
                    {esTI && (
                      <td className="px-3 py-2">
                        <div className="flex justify-end gap-1">
                          <Button
                            icon="pi pi-history"
                            className="p-button-text p-button-sm"
                            aria-label={`Historial ${nombre}`}
                            tooltip="Historial"
                            onClick={() => setHistorial(r)}
                          />
                          <Button
                            icon="pi pi-user-edit"
                            className="p-button-text p-button-sm"
                            aria-label={`Reasignar ${nombre}`}
                            tooltip="Reasignar"
                            onClick={() => setReasignando(r)}
                          />
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
              {visibles.length === 0 && (
                <tr>
                  <td colSpan={esTI ? 5 : 4} className="px-3 py-6 text-center text-gray-500">
                    Ninguna tienda coincide con los filtros.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {reasignando && (
        <ReasignarDialog
          responsable={reasignando}
          supervisores={opciones.supervisores}
          regionales={opciones.regionales}
          onHide={() => setReasignando(null)}
          onCambio={() => void cargar()}
        />
      )}
      {historial && <HistorialDialog tienda={historial} onHide={() => setHistorial(null)} />}
    </div>
  );
};

export default ResponsablesScreen;
